import React from 'react';
import { motion } from 'framer-motion';
import { Crown, CalendarDays, Clock, MapPin, Ticket, ArrowRight } from 'lucide-react';

/**
 * Time-limited sponsorship banner — IIY Phytoventra is the Official Wellbeing
 * Partner of the Miss Goa International UK 2026 Grand Finale.
 *
 * AUTO-EXPIRY: renders nothing after midnight at the end of Sat 6 Sep 2026
 * (UK time). 7 Sep 2026 00:00 BST == 6 Sep 2026 23:00 UTC. No manual removal
 * needed — once the cutoff passes the section simply stops rendering.
 */
const EXPIRES_AT = new Date('2026-09-06T23:00:00Z');
const EVENT_URL = 'https://www.missgoainternational.com';

export default function MissGoaSponsorBanner() {
  if (new Date() >= EXPIRES_AT) return null;

  const details = [
    { icon: CalendarDays, label: 'Sunday, 6 September 2026' },
    { icon: Clock, label: '4:00 PM onwards' },
    { icon: MapPin, label: 'Radisson RED London Heathrow (UB7 0DU)' },
  ];

  return (
    <section
      className="relative overflow-hidden bg-gradient-to-br from-emerald-950 via-emerald-900 to-emerald-950 border-y border-amber-300/25"
      data-testid="miss-goa-sponsor-banner"
    >
      {/* soft gold glow accents */}
      <div className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full bg-amber-400/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-amber-500/10 blur-3xl" />

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-12">
        <div className="grid md:grid-cols-[180px_1fr] gap-6 md:gap-10 items-center">
          {/* Poster */}
          <a
            href={EVENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block mx-auto md:mx-0 w-[150px] md:w-[180px] shrink-0"
            data-testid="miss-goa-poster-link"
          >
            <motion.img
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              src="/miss-goa-poster.webp"
              alt="Miss Goa International UK 2026 Grand Finale — 6 September 2026, Radisson RED London Heathrow"
              className="w-full rounded-xl shadow-2xl ring-1 ring-amber-300/30"
              loading="lazy"
            />
          </a>

          {/* Content */}
          <div className="text-center md:text-left">
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 border border-amber-300/30 px-3 py-1 text-[10px] md:text-[11px] uppercase tracking-[0.18em] font-bold text-amber-200"
              data-testid="miss-goa-eyebrow"
            >
              <Crown className="w-3.5 h-3.5" /> Official Wellbeing Partner
            </span>

            <h2 className="mt-3 text-2xl md:text-3xl font-bold font-heading text-white leading-tight">
              Join us at the <span className="text-amber-300">Miss Goa International</span> Grand Finale 2026
            </h2>
            <p className="mt-2 text-sm md:text-base text-emerald-100/90 leading-relaxed max-w-2xl">
              IIY Phytoventra Ltd is proud to be the Official Wellbeing Partner for the Miss Goa
              International UK 2026 Grand Finale &amp; After Party — a celebration of Goan heritage,
              culture and talent on an international stage in London.
            </p>

            {/* Event details */}
            <ul className="mt-4 flex flex-wrap justify-center md:justify-start gap-x-5 gap-y-2">
              {details.map((d) => (
                <li key={d.label} className="flex items-center gap-2 text-[13px] md:text-sm text-emerald-50/90">
                  <d.icon className="w-4 h-4 text-amber-300 shrink-0" />
                  <span>{d.label}</span>
                </li>
              ))}
            </ul>

            {/* Offer + CTA */}
            <div className="mt-6 flex flex-col sm:flex-row items-center gap-4 justify-center md:justify-start">
              <div
                className="flex items-center gap-2.5 rounded-xl bg-white/5 border border-amber-300/30 px-4 py-2.5"
                data-testid="miss-goa-promo"
              >
                <Ticket className="w-4 h-4 text-amber-300" />
                <span className="text-sm text-emerald-50">
                  <strong className="text-amber-300">£5 OFF</strong> your ticket · code{' '}
                  <strong className="tracking-wide text-white">IIYPV</strong>
                </span>
              </div>
              <a
                href={EVENT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-emerald-950 font-bold py-2.5 px-5 text-sm hover:bg-amber-300 transition-colors shadow-lg"
                data-testid="miss-goa-book-btn"
              >
                Book tickets <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <p className="mt-3 text-[11px] text-emerald-200/70">
              Apply promo code <strong>IIYPV</strong> at checkout on missgoainternational.com.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
